import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthProvider';
import { useScreen } from '../lib/useScreen';
import { calcularMatches } from '../lib/matches';
import { solicitarConexion, estadoConexion } from '../lib/conexiones';
import SidebarEscritorio from '../components/sala/SidebarEscritorio';

const font = "'DM Sans', sans-serif";
const serif = "'DM Serif Display', serif";
const C = { turquesa:'#5dc1b9', mango:'#FDBE02', coral:'#eb636b', morado:'#534AB7', moradoClaro:'#AFA9EC' };

export default function MatchesPage() {
  const { user, profile, membership } = useAuth();
  const { isMobile, w } = useScreen();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const isFree = !membership || membership === 'free_observer' || membership === 'tu_radar';

  const [conexionStates, setConexionStates] = useState({});

  useEffect(() => {
    if (!user?.id) return;
    calcularMatches(user.id).then(async (data) => {
      setMatches(data);
      // Load connection states for each match
      const states = {};
      for (const m of data) {
        const con = await estadoConexion(user.id, m.profile.id);
        if (con) states[m.profile.id] = con;
      }
      setConexionStates(states);
      setLoading(false);
    });
  }, [user?.id]);

  const handleConectar = async (otroId) => {
    const { data, error } = await solicitarConexion(otroId);
    if (error) { alert('Error: ' + error.message); return; }
    setConexionStates(prev => ({ ...prev, [otroId]: { ...data, user_from_id: user.id } }));
  };

  const scoreColor = (score) => {
    if (score >= 70) return C.turquesa;
    if (score >= 40) return C.mango;
    return 'rgba(255,255,255,0.5)';
  };

  return (
    <div style={{ background:'#0a0a0a', minHeight:'100vh', fontFamily:font, color:'#fff' }}>
      <header style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'11px 16px', borderBottom:'0.5px solid rgba(255,255,255,0.06)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          {isMobile && <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{ background:'none', border:'none', color:'#fff', fontSize:18, cursor:'pointer' }}>☰</button>}
          <span style={{ fontFamily:serif, fontSize:17, background:'linear-gradient(90deg, #5dc1b9, #FDBE02, #eb636b)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text' }}>T&D</span>
          <span style={{ fontSize:11, padding:'2px 8px', borderRadius:999, background:'rgba(83,74,183,0.18)', color:C.moradoClaro }}>Matches</span>
        </div>
        <div style={{ width:28, height:28, borderRadius:'50%', background:'linear-gradient(135deg, #534AB7, #1D9E75)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:600 }}>
          {(profile?.full_name || 'U')[0]}
        </div>
      </header>

      <div style={{ display:'flex', minHeight:'calc(100vh - 50px)' }}>
        {w >= 900 && (sidebarOpen || !isMobile) && (
          <SidebarEscritorio membership={membership} currentView="matches" />
        )}

        <main style={{ flex:1, padding:isMobile?'16px 12px':'20px 24px', overflowY:'auto' }}>
          <div style={{ marginBottom:24 }}>
            <p style={{ fontSize:10, color:'rgba(93,193,185,0.8)', letterSpacing:1.5, textTransform:'uppercase', margin:'0 0 4px' }}>Conexiones potenciales</p>
            <h1 style={{ fontFamily:serif, fontSize:22, fontWeight:400, margin:'0 0 6px' }}>
              {matches.length > 0 ? `${matches.length} matches encontrados` : 'Buscando matches...'}
            </h1>
            <p style={{ margin:0, fontSize:14, color:'rgba(255,255,255,0.55)', lineHeight:1.6 }}>Personas y proyectos que resuenan contigo</p>
          </div>

          {/* Loading */}
          {loading && (
            <div style={{ textAlign:'center', padding:'60px 0' }}>
              <div style={{ width:28, height:28, border:'3px solid rgba(255,255,255,0.08)', borderTopColor:C.turquesa, borderRadius:'50%', animation:'tdSpin 0.8s linear infinite', margin:'0 auto 12px' }} />
              <p style={{ fontSize:14, color:'rgba(255,255,255,0.4)' }}>Creando tu experiencia...</p>
              <style>{`@keyframes tdSpin{to{transform:rotate(360deg)}}`}</style>
            </div>
          )}

          {/* Match cards */}
          {!loading && matches.length > 0 && (
            <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))', gap:16, marginBottom:24 }}>
              {matches.map((match, i) => {
                const p = match.profile;
                const nombre = p.business_name || p.full_name || 'Emprendedor';
                const firstName = (p.full_name || 'E').split(' ')[0];
                const stage = p.business_stage || '';
                const industry = p.business_type || '';
                const pill = [industry, stage].filter(Boolean).join(' · ');

                return (
                  <div key={p.id} style={{
                    background:'rgba(255,255,255,0.05)',
                    border:'1px solid rgba(255,255,255,0.1)',
                    borderRadius:20,
                    padding:isMobile?'20px 16px':'24px',
                    transition:'border-color 0.3s, transform 0.3s',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(93,193,185,0.3)'; e.currentTarget.style.transform='translateY(-2px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(255,255,255,0.1)'; e.currentTarget.style.transform='translateY(0)'; }}
                  >
                    <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:16 }}>
                      {/* Avatar */}
                      {isFree ? (
                        <div style={{ width:52, height:52, borderRadius:'50%', background:'linear-gradient(135deg, rgba(150,150,170,0.4), rgba(100,100,120,0.3))', filter:'blur(6px)', flexShrink:0 }} />
                      ) : p.avatar_url ? (
                        <img src={p.avatar_url} alt="" style={{ width:52, height:52, borderRadius:'50%', objectFit:'cover', flexShrink:0, border:'2px solid rgba(255,255,255,0.1)' }} />
                      ) : (
                        <div style={{ width:52, height:52, borderRadius:'50%', background:'linear-gradient(135deg, #5dc1b9, #FDBE02)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, fontWeight:600, flexShrink:0 }}>
                          {firstName[0]}
                        </div>
                      )}

                      <div style={{ flex:1, minWidth:0 }}>
                        <p style={{ margin:0, fontSize:16, fontWeight:500, color:'#fff' }}>
                          {isFree ? '••••••••' : nombre}
                        </p>
                        {pill && (
                          <span style={{ fontSize:11, color:'rgba(255,255,255,0.5)', display:'inline-block', marginTop:2 }}>{pill}</span>
                        )}
                        {p.city && !isFree && (
                          <p style={{ margin:'2px 0 0', fontSize:11, color:'rgba(255,255,255,0.4)' }}>📍 {p.city}</p>
                        )}
                      </div>

                      {/* Score */}
                      <div style={{ textAlign:'center', flexShrink:0 }}>
                        <span style={{ fontSize:28, fontWeight:700, fontFamily:serif, color:scoreColor(match.score) }}>{match.score}</span>
                        <p style={{ margin:0, fontSize:9, color:'rgba(255,255,255,0.4)', textTransform:'uppercase', letterSpacing:0.5 }}>match</p>
                      </div>
                    </div>

                    {/* Score bar */}
                    <div style={{ height:4, borderRadius:2, background:'rgba(255,255,255,0.08)', marginBottom:14 }}>
                      <div style={{ height:'100%', borderRadius:2, width:`${match.score}%`, background:`linear-gradient(90deg, ${scoreColor(match.score)}, ${C.mango})`, transition:'width 0.5s ease' }} />
                    </div>

                    {/* Razones */}
                    <div style={{ display:'flex', flexDirection:'column', gap:4, marginBottom:14 }}>
                      {match.razones.slice(0, 3).map((r, j) => (
                        <p key={j} style={{ margin:0, fontSize:12, color:'rgba(255,255,255,0.55)', display:'flex', alignItems:'center', gap:6 }}>
                          <span>{r.emoji}</span> {r.text}
                        </p>
                      ))}
                      {match.razones.length === 0 && (
                        <p style={{ margin:0, fontSize:12, color:'rgba(255,255,255,0.35)', fontStyle:'italic' }}>Perfil compatible</p>
                      )}
                    </div>

                    {/* Action */}
                    {isFree ? (
                      <button onClick={() => navigate('/membresias')} style={{
                        width:'100%', padding:'10px 0', background:'rgba(255,255,255,0.04)',
                        border:'0.5px solid rgba(253,190,2,0.25)', borderRadius:10,
                        color:C.mango, fontSize:12, fontWeight:500, cursor:'pointer', fontFamily:'inherit',
                      }}>
                        🔒 Conecta con Spark
                      </button>
                    ) : (() => {
                      const con = conexionStates[p.id];
                      if (con?.estado === 'aceptada') return (
                        <button onClick={() => navigate('/chat/' + con.id)} style={{ width:'100%', padding:'10px 0', background:C.turquesa, border:'none', borderRadius:10, color:'#fff', fontSize:12, fontWeight:500, cursor:'pointer', fontFamily:'inherit' }}>
                          💬 Abrir chat
                        </button>
                      );
                      if (con?.estado === 'pendiente' && con.user_from_id === user?.id) return (
                        <button disabled style={{ width:'100%', padding:'10px 0', background:'rgba(255,255,255,0.04)', border:'0.5px solid rgba(253,190,2,0.2)', borderRadius:10, color:C.mango, fontSize:12, cursor:'default', fontFamily:'inherit', opacity:0.7 }}>
                          ⏳ Solicitud enviada
                        </button>
                      );
                      if (con?.estado === 'pendiente' && con.user_to_id === user?.id) return (
                        <button onClick={() => navigate('/conexiones')} style={{ width:'100%', padding:'10px 0', background:'rgba(253,190,2,0.1)', border:'0.5px solid rgba(253,190,2,0.3)', borderRadius:10, color:C.mango, fontSize:12, fontWeight:500, cursor:'pointer', fontFamily:'inherit' }}>
                          📩 Te solicito conexion
                        </button>
                      );
                      if (con?.estado === 'rechazada') return (
                        <button disabled style={{ width:'100%', padding:'10px 0', background:'rgba(255,255,255,0.03)', border:'0.5px solid rgba(255,255,255,0.08)', borderRadius:10, color:'rgba(255,255,255,0.3)', fontSize:12, cursor:'default', fontFamily:'inherit' }}>
                          Conexion no disponible
                        </button>
                      );
                      return (
                        <div style={{ display:'flex', gap:6 }}>
                          <button onClick={() => handleConectar(p.id)} style={{ flex:1, padding:'10px 0', background:'rgba(93,193,185,0.1)', border:'0.5px solid rgba(93,193,185,0.25)', borderRadius:10, color:C.turquesa, fontSize:12, fontWeight:500, cursor:'pointer', fontFamily:'inherit' }}>
                            🤝 Conectar
                          </button>
                          <button onClick={() => navigate('/proyecto/' + p.id)} style={{ padding:'10px 14px', background:'rgba(255,255,255,0.04)', border:'0.5px solid rgba(255,255,255,0.1)', borderRadius:10, color:'rgba(255,255,255,0.5)', fontSize:12, cursor:'pointer', fontFamily:'inherit' }}>
                            👤
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          )}

          {/* Empty state */}
          {!loading && matches.length === 0 && (
            <div style={{ textAlign:'center', padding:'60px 20px' }}>
              <p style={{ fontSize:48, margin:'0 0 12px' }}>🔭</p>
              <p style={{ fontFamily:serif, fontSize:18, margin:'0 0 8px' }}>Aun no hay matches</p>
              <p style={{ fontSize:14, color:'rgba(255,255,255,0.5)', margin:'0 0 20px' }}>Cuando mas emprendedores se unan, aparecaran aqui tus conexiones potenciales.</p>
            </div>
          )}

          {/* Upgrade CTA for free */}
          {isFree && matches.length > 0 && (
            <div style={{
              background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)',
              borderRadius:20, padding:28, textAlign:'center',
            }}>
              <p style={{ margin:'0 0 6px', fontFamily:serif, fontSize:18 }}>Desbloquea nombres, fotos y contacto</p>
              <p style={{ margin:'0 0 18px', fontSize:13, color:'rgba(255,255,255,0.5)', lineHeight:1.6 }}>Con Doer puedes ver quienes son y conectar directamente.</p>
              <button onClick={() => navigate('/membresias')} style={{
                padding:'12px 32px', background:'linear-gradient(135deg, #534AB7, #1D9E75)',
                border:'none', borderRadius:50, color:'#fff', fontSize:14, fontWeight:600,
                cursor:'pointer', fontFamily:'inherit',
              }}>
                Doer $139/mes →
              </button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
